import { AlertTriangle } from "lucide-react";
import { Input } from "@/frontend/components/ui/input";
import { Label } from "@/frontend/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/frontend/components/ui/select";
import { SearchableSelect } from "@/frontend/components/ui/searchable-select";
import { BirthDatePicker } from "@/frontend/components/ui/birthdate-picker";
import { cn } from "@/frontend/lib/utils";
import { COUNTRIES, NATIONALITIES } from "@/shared/data/geo";
import {
  DOCUMENT_TYPE_OPTIONS,
  OCCUPATION_OPTIONS,
  type GuestFieldErrors,
} from "@/shared/constants/guestForm";
import type { GuestInfo } from "@/shared/types";

export type { GuestFieldErrors };

interface GuestFieldsProps {
  guest: GuestInfo;
  index: number;
  errors?: GuestFieldErrors;
  onUpdate: (field: keyof GuestInfo, value: string) => void;
  /** Show the "this email is used for your contract" notice (public form). */
  showEmailNotice?: boolean;
  /**
   * Make name and email read-only. Used when an existing student fills in the
   * details missing from their booking: those two are their login identity and
   * appear on the signed contract, so only the admin may change them.
   */
  lockIdentity?: boolean;
}

const FieldError = ({ message }: { message?: string }) =>
  message ? <p className="text-sm text-destructive mt-1">{message}</p> : null;

/**
 * Full per-guest personal-information fieldset collected at booking (DEV2-755).
 * Shared by the public booking form and the admin create/edit dialog; the
 * `errors` prop is optional so callers that don't validate (admin) can omit it.
 */
export function GuestFields({
  guest,
  index: i,
  errors,
  onUpdate,
  showEmailNotice = true,
  lockIdentity = false,
}: GuestFieldsProps) {
  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor={`name-${i}`}>Full Name *</Label>
        <Input
          id={`name-${i}`}
          value={guest.name}
          onChange={(e) => onUpdate("name", e.target.value)}
          className={cn(errors?.name && "border-destructive")}
          placeholder="Enter full name"
          disabled={lockIdentity}
        />
        <FieldError message={errors?.name} />
      </div>

      <div>
        <Label htmlFor={`email-${i}`}>Email *</Label>
        <Input
          id={`email-${i}`}
          type="email"
          value={guest.email}
          onChange={(e) => onUpdate("email", e.target.value)}
          className={cn(errors?.email && "border-destructive")}
          placeholder="student@email.com"
          disabled={lockIdentity}
        />
        {showEmailNotice && (
          <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600 mt-0.5" aria-hidden="true" />
            <p>
              <span className="font-semibold">Important:</span> This email will be used for your contract and all communication during your stay. Please use an email address you'll keep access to.
            </p>
          </div>
        )}
        <FieldError message={errors?.email} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor={`phone-${i}`}>Mobile *</Label>
          <Input
            id={`phone-${i}`}
            type="tel"
            value={guest.phone}
            onChange={(e) => onUpdate("phone", e.target.value)}
            className={cn(errors?.phone && "border-destructive")}
            placeholder="+351 XXX XXX XXX"
          />
          <FieldError message={errors?.phone} />
        </div>
        <div>
          <Label htmlFor={`emergency-${i}`}>Emergency Contact *</Label>
          <Input
            id={`emergency-${i}`}
            type="tel"
            value={guest.emergencyContact ?? ""}
            onChange={(e) => onUpdate("emergencyContact", e.target.value)}
            className={cn(errors?.emergencyContact && "border-destructive")}
            placeholder="+351 XXX XXX XXX"
          />
          <FieldError message={errors?.emergencyContact} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor={`dob-${i}`}>Date of Birth *</Label>
          <BirthDatePicker
            id={`dob-${i}`}
            value={guest.dateOfBirth}
            onChange={(v) => onUpdate("dateOfBirth", v)}
            invalid={!!errors?.dateOfBirth}
          />
          <FieldError message={errors?.dateOfBirth} />
        </div>
        <div>
          <Label htmlFor={`occupation-${i}`}>Occupation *</Label>
          <Select
            value={guest.occupation || undefined}
            onValueChange={(v) => onUpdate("occupation", v)}
          >
            <SelectTrigger
              id={`occupation-${i}`}
              className={cn(errors?.occupation && "border-destructive")}
            >
              <SelectValue placeholder="Select occupation" />
            </SelectTrigger>
            <SelectContent>
              {OCCUPATION_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldError message={errors?.occupation} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor={`nationality-${i}`}>Nationality *</Label>
          <SearchableSelect
            id={`nationality-${i}`}
            value={guest.nationality}
            onChange={(v) => onUpdate("nationality", v)}
            options={NATIONALITIES}
            placeholder="Select nationality"
            searchPlaceholder="Search nationality..."
            emptyText="No nationality found."
            invalid={!!errors?.nationality}
          />
          <FieldError message={errors?.nationality} />
        </div>
        <div>
          <Label htmlFor={`country-${i}`}>Country *</Label>
          <SearchableSelect
            id={`country-${i}`}
            value={guest.country}
            onChange={(v) => onUpdate("country", v)}
            options={COUNTRIES}
            placeholder="Select country"
            searchPlaceholder="Search country..."
            emptyText="No country found."
            invalid={!!errors?.country}
          />
          <FieldError message={errors?.country} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor={`docType-${i}`}>Document type *</Label>
          <Select
            value={guest.documentType || undefined}
            onValueChange={(v) => onUpdate("documentType", v)}
          >
            <SelectTrigger
              id={`docType-${i}`}
              className={cn(errors?.documentType && "border-destructive")}
            >
              <SelectValue placeholder="Select document type" />
            </SelectTrigger>
            <SelectContent>
              {DOCUMENT_TYPE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldError message={errors?.documentType} />
        </div>
        <div>
          <Label htmlFor={`docNumber-${i}`}>ID / Passport Nr *</Label>
          <Input
            id={`docNumber-${i}`}
            maxLength={20}
            value={guest.documentNumber ?? ""}
            onChange={(e) => onUpdate("documentNumber", e.target.value)}
            className={cn(errors?.documentNumber && "border-destructive")}
            placeholder="Document number"
          />
          <FieldError message={errors?.documentNumber} />
        </div>
      </div>

      <div>
        <Label htmlFor={`taxNumber-${i}`}>Portuguese Tax Number *</Label>
        <Input
          id={`taxNumber-${i}`}
          inputMode="numeric"
          maxLength={9}
          value={guest.taxNumber ?? ""}
          onChange={(e) => onUpdate("taxNumber", e.target.value)}
          className={cn(errors?.taxNumber && "border-destructive")}
          placeholder="999999990"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Insert 999 999 990 if not applicable.
        </p>
        <FieldError message={errors?.taxNumber} />
      </div>

      <div>
        <Label htmlFor={`address-${i}`}>Address *</Label>
        <Input
          id={`address-${i}`}
          value={guest.address ?? ""}
          onChange={(e) => onUpdate("address", e.target.value)}
          className={cn(errors?.address && "border-destructive")}
          placeholder="Street and number"
        />
        <FieldError message={errors?.address} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor={`postalCode-${i}`}>Postal code *</Label>
          <Input
            id={`postalCode-${i}`}
            value={guest.postalCode ?? ""}
            onChange={(e) => onUpdate("postalCode", e.target.value)}
            className={cn(errors?.postalCode && "border-destructive")}
            placeholder="0000-000"
          />
          <FieldError message={errors?.postalCode} />
        </div>
        <div>
          <Label htmlFor={`city-${i}`}>City *</Label>
          <Input
            id={`city-${i}`}
            value={guest.city ?? ""}
            onChange={(e) => onUpdate("city", e.target.value)}
            className={cn(errors?.city && "border-destructive")}
            placeholder="City"
          />
          <FieldError message={errors?.city} />
        </div>
      </div>
    </div>
  );
}
